import { useEffect, useState, useMemo, useCallback } from "react";
import { DatePicker, Form, Select, Spin, Modal, message } from "antd";
import dayjs from "dayjs";
import {
  tournamentFormatService,
  matchTeamService,
  disciplineService,
  subCategoryService,
} from "@/frontend/services";
import type {
  MatchTeamResponseDTO,
  DisciplineResponseDTO,
  SubCategoryResponseDTO,
} from "@/shared/dto";

interface MatchCreateModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

async function fetchAllMatchTeams(params?: any): Promise<MatchTeamResponseDTO[]> {
  const firstPage = await matchTeamService.getPaginate({ page: 1, pageSize: 100, ...params });
  if (!firstPage.success || !firstPage.data) return [];

  const teams = [...firstPage.data.data];
  const totalPages = firstPage.data.pagination.totalPages;

  if (totalPages > 1) {
    const remaining = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, i) =>
        matchTeamService.getPaginate({ page: i + 2, pageSize: 100, ...params }),
      ),
    );
    for (const page of remaining) {
      if (page.success && page.data) teams.push(...page.data.data);
    }
  }

  return teams;
}

export function MatchCreateModal({ open, onClose, onCreated }: MatchCreateModalProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState<MatchTeamResponseDTO[]>([]);
  const [disciplines, setDisciplines] = useState<DisciplineResponseDTO[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategoryResponseDTO[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(false);

  const selectedSport = Form.useWatch("sport", form);
  const selectedSubCategory = Form.useWatch("subCategoryId", form);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      disciplineService.getPaginate({ page: 1, pageSize: 100 }),
      subCategoryService.getPaginate({ page: 1, pageSize: 100 }),
    ])
      .then(([discRes, subCatsRes]) => {
        if (discRes.success && discRes.data) setDisciplines(discRes.data.data);
        if (subCatsRes.success && subCatsRes.data) setSubCategories(subCatsRes.data.data);
      })
      .catch(() => message.error("Error al cargar datos"))
      .finally(() => setLoading(false));
  }, [open]);

  const loadTeams = useCallback(async (subCategoryId?: string) => {
    setLoadingTeams(true);
    setTeams([]);
    form.setFieldsValue({ homeTeamId: undefined, awayTeamId: undefined });
    try {
      const params: any = {};
      if (subCategoryId) params.subCategoryId = subCategoryId;
      const loaded = await fetchAllMatchTeams(params);
      setTeams(loaded);
    } catch {
      message.error("Error al cargar equipos");
    } finally {
      setLoadingTeams(false);
    }
  }, [form]);

  useEffect(() => {
    if (selectedSubCategory) {
      loadTeams(selectedSubCategory);
    }
  }, [selectedSubCategory, loadTeams]);

  const teamOptions = useMemo(
    () => teams.map((t) => ({ label: t.name, value: t.id })),
    [teams],
  );

  const filteredSubCategories = useMemo(() => {
    if (!selectedSport) return subCategories;
    return subCategories.filter((s) => {
      const formats = (s as any).formats;
      return true;
    });
  }, [subCategories, selectedSport]);

  function handleClose() {
    form.resetFields();
    setTeams([]);
    onClose();
  }

  async function handleSave() {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const homeTeam = teams.find((t) => t.id === values.homeTeamId);
      const awayTeam = teams.find((t) => t.id === values.awayTeamId);

      const matchDate = values.matchDate;
      const dateStr = matchDate.format("YYYY-MM-DD");
      const timeStr = matchDate.toISOString();

      const formatName = `${homeTeam?.name ?? "Local"} vs ${awayTeam?.name ?? "Visitante"}`;

      await tournamentFormatService.create({
        name: formatName,
        sport: values.sport,
        config: {
          subCategoryId: values.subCategoryId,
          matches: [
            {
              homeTeamId: values.homeTeamId,
              awayTeamId: values.awayTeamId,
              date: dateStr,
              time: timeStr,
              homeScore: 0,
              awayScore: 0,
              status: "scheduled",
              order: 1,
            },
          ],
        },
      });

      message.success("Partido creado correctamente");
      form.resetFields();
      setTeams([]);
      onCreated();
    } catch {
      message.error("Error al crear el partido");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Agregar Partido"
      open={open}
      onOk={handleSave}
      onCancel={handleClose}
      okText="Crear"
      cancelText="Cancelar"
      confirmLoading={saving}
      width={520}
      destroyOnHidden
    >
      {loading ? (
        <div className="flex justify-center py-8">
          <Spin tip="Cargando..." />
        </div>
      ) : (
        <Form form={form} layout="vertical" className="pt-2">
          <Form.Item
            name="sport"
            label="Deporte"
            rules={[{ required: true, message: "Selecciona un deporte" }]}
          >
            <Select
              placeholder="Seleccionar deporte"
              showSearch
              filterOption={(input, opt) =>
                String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={disciplines.map((d) => ({ label: d.name, value: d.name }))}
            />
          </Form.Item>

          <Form.Item
            name="subCategoryId"
            label="Categoría"
            rules={[{ required: true, message: "Selecciona una categoría" }]}
          >
            <Select
              placeholder="Seleccionar categoría"
              showSearch
              filterOption={(input, opt) =>
                String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              options={filteredSubCategories.map((s) => ({ label: s.category, value: s.id }))}
            />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="homeTeamId"
              label="Equipo Local"
              rules={[{ required: true, message: "Selecciona el local" }]}
            >
              <Select
                placeholder={loadingTeams ? "Cargando equipos..." : "Seleccionar categoría primero"}
                showSearch
                loading={loadingTeams}
                disabled={!selectedSubCategory || loadingTeams}
                notFoundContent={loadingTeams ? <Spin size="small" /> : "No hay equipos para esta categoría"}
                filterOption={(input, opt) =>
                  String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={teamOptions}
              />
            </Form.Item>

            <Form.Item
              name="awayTeamId"
              label="Equipo Visitante"
              rules={[{ required: true, message: "Selecciona el visitante" }]}
            >
              <Select
                placeholder={loadingTeams ? "Cargando equipos..." : "Seleccionar categoría primero"}
                showSearch
                loading={loadingTeams}
                disabled={!selectedSubCategory || loadingTeams}
                notFoundContent={loadingTeams ? <Spin size="small" /> : "No hay equipos para esta categoría"}
                filterOption={(input, opt) =>
                  String(opt?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={teamOptions}
              />
            </Form.Item>
          </div>

          <Form.Item
            name="matchDate"
            label="Fecha y Hora"
            rules={[{ required: true, message: "Selecciona fecha y hora" }]}
          >
            <DatePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              className="w-full"
              placeholder="Seleccionar fecha y hora"
              disabledDate={(current) => current && current < dayjs().startOf("day")}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
